// Rules for VALIDATION_CONSTRAINTS.md section 4 — gateway semantics.
//
// A gateway's branching logic lives in node.data.conditions (path -> expression)
// and node.data.outputs (labels). The default path is the condition with an
// empty/whitespace expression. ParallelGateway carries neither conditions nor
// default. Outgoing edges carry sourceHandle === String(cond.path) to bind
// a branch to a real edge (matching the export logic in utils/pssHelper.ts).
//
// Intentionally skipped in this file:
//   - 4.2 (fork cardinality) and 4.3 (merge cardinality): fully covered by
//     rules 3.7 / 3.8 in section_3_cardinality.ts. Duplicating here would
//     double-report.
//   - 4.12 (expressions evaluate to boolean): requires running the script
//     engine against live context; cannot be statically verified. TODO below.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import {
  buildLookup,
  getFlowIncoming,
  getFlowOutgoing,
  getOutgoing,
  getIncoming,
  isGateway,
  isForkGateway,
  isMergeGateway,
  labelOf,
} from '../helpers';

const VALID_GATEWAY_TYPES = new Set(['XorGateway', 'InclusiveGateway', 'ParallelGateway']);

type GatewayCondition = { path: number; expression: string };

const getConditions = (n: any): GatewayCondition[] =>
  (n?.data?.conditions ?? []) as GatewayCondition[];

const isEmptyExpression = (expr: string | undefined | null): boolean =>
  !expr || expr.trim() === '';

// 4.1 — Gateway type must be Xor | Inclusive | Parallel.
const rule_4_1_GatewayKind: ValidationRule = {
  id: '4.1',
  description: 'Gateway Kind must be XorGateway, InclusiveGateway, or ParallelGateway',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isGateway(n)) continue;
      if (!n.type || !VALID_GATEWAY_TYPES.has(n.type)) {
        issues.push({
          severity: 'error',
          rule: '4.1',
          message: `${labelOf(n)} has unsupported gateway kind "${n.type ?? '<none>'}"; must be XorGateway, InclusiveGateway, or ParallelGateway.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_1_GatewayKind);

// 4.2 — skipped (covered by 3.7).
// 4.3 — skipped (covered by 3.8).

// 4.4 — A gateway with both >1 incoming AND >1 outgoing is invalid regardless
// of the IsMerging flag. Gateway role is strictly fork XOR merge.
const rule_4_4_GatewayNotBothForkAndMerge: ValidationRule = {
  id: '4.4',
  description: 'Gateway cannot both fork (>1 outgoing) and merge (>1 incoming) simultaneously',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isGateway(n)) continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (incoming > 1 && outgoing > 1) {
        issues.push({
          severity: 'error',
          rule: '4.4',
          message: `${labelOf(n)} has ${incoming} incoming and ${outgoing} outgoing flows; a gateway must be either fork or merge, not both.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_4_GatewayNotBothForkAndMerge);

// 4.5 — For Xor/Inclusive fork: every condition path must resolve to an
// outgoing edge whose sourceHandle === String(path).
const rule_4_5_ExpressionKeysResolve: ValidationRule = {
  id: '4.5',
  description: 'Every condition path on a Xor/Inclusive fork must match an outgoing edge sourceHandle',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isForkGateway(n)) continue;
      if (n.type !== 'XorGateway' && n.type !== 'InclusiveGateway') continue;
      const conds = getConditions(n);
      const outgoing = getOutgoing(lookup, n.id);
      for (const cond of conds) {
        const handle = String(cond.path);
        const hit = outgoing.some(e => e.sourceHandle === handle);
        if (!hit) {
          issues.push({
            severity: 'error',
            rule: '4.5',
            message: `${labelOf(n)} condition path ${cond.path} has no matching outgoing edge (sourceHandle="${handle}").`,
            nodeIds: [n.id],
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_4_5_ExpressionKeysResolve);

// 4.6 — DefaultPath target must exist: the outgoing edge whose matching
// condition has empty expression must have a non-empty target.
const rule_4_6_DefaultPathTarget: ValidationRule = {
  id: '4.6',
  description: 'Default outgoing edge on Xor/Inclusive gateway must have a target',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'XorGateway' && n.type !== 'InclusiveGateway') continue;
      const conds = getConditions(n);
      const defaultConds = conds.filter(c => isEmptyExpression(c.expression));
      if (defaultConds.length === 0) continue;
      const outgoing = getOutgoing(lookup, n.id);
      for (const cond of defaultConds) {
        const handle = String(cond.path);
        const matching = outgoing.find(e => e.sourceHandle === handle);
        if (matching && !matching.target) {
          issues.push({
            severity: 'error',
            rule: '4.6',
            message: `${labelOf(n)} default edge ${matching.id} (path ${cond.path}) has no target; DefaultPath would be unresolvable.`,
            nodeIds: [n.id],
            edgeIds: [matching.id],
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_4_6_DefaultPathTarget);

// 4.7 — XorGateway has at most one default (empty-expression) condition.
const rule_4_7_XorAtMostOneDefault: ValidationRule = {
  id: '4.7',
  description: 'XorGateway must have at most one default (empty-expression) condition',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'XorGateway') continue;
      const conds = getConditions(n);
      const defaults = conds.filter(c => isEmptyExpression(c.expression));
      if (defaults.length > 1) {
        issues.push({
          severity: 'error',
          rule: '4.7',
          message: `${labelOf(n)} has ${defaults.length} default conditions; at most one is allowed.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_7_XorAtMostOneDefault);

// 4.8 — ParallelGateway must NOT have conditions with non-empty expressions,
// and must not carry a default path (no empty-expression condition either).
const rule_4_8_ParallelNoConditions: ValidationRule = {
  id: '4.8',
  description: 'ParallelGateway must not have conditions or default paths',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'ParallelGateway') continue;
      const conds = getConditions(n);
      const offending = conds.filter(c => !isEmptyExpression(c.expression));
      if (offending.length > 0) {
        issues.push({
          severity: 'error',
          rule: '4.8',
          message: `${labelOf(n)} is a ParallelGateway and must not carry expressions (found ${offending.length}).`,
          nodeIds: [n.id],
        });
      }
      // Any outgoing edge flagged default (via empty-expression matching condition)
      // also indicates a modeling error — engine ignores it but UI would leak it.
      const outgoing = getOutgoing(lookup, n.id);
      const defaultEdges = outgoing.filter(e => {
        const cond = conds.find(c => String(c.path) === e.sourceHandle);
        return cond && isEmptyExpression(cond.expression);
      });
      if (defaultEdges.length > 0) {
        issues.push({
          severity: 'error',
          rule: '4.8',
          message: `${labelOf(n)} is a ParallelGateway and must not have default-path edges (found ${defaultEdges.length}).`,
          nodeIds: [n.id],
          edgeIds: defaultEdges.map(e => e.id),
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_8_ParallelNoConditions);

// 4.9 — InclusiveGateway fork must have at least one non-empty expression.
// Engine returns empty paths otherwise (TODO in engine: no default fallback).
const rule_4_9_InclusiveForkHasExpression: ValidationRule = {
  id: '4.9',
  description: 'InclusiveGateway fork must have at least one non-empty expression',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'InclusiveGateway') continue;
      if (!isForkGateway(n)) continue;
      const conds = getConditions(n);
      const hasReal = conds.some(c => !isEmptyExpression(c.expression));
      if (!hasReal) {
        issues.push({
          severity: 'error',
          rule: '4.9',
          message: `${labelOf(n)} is an InclusiveGateway fork with no non-empty expressions; engine returns empty path set.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_9_InclusiveForkHasExpression);

// 4.10 — XorGateway fork must have either at least one non-empty expression
// OR a default (empty-expression) condition. Otherwise token silently stalls.
const rule_4_10_XorForkHasRoute: ValidationRule = {
  id: '4.10',
  description: 'XorGateway fork must have at least one expression or a default path',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'XorGateway') continue;
      if (!isForkGateway(n)) continue;
      const conds = getConditions(n);
      if (conds.length === 0) {
        issues.push({
          severity: 'error',
          rule: '4.10',
          message: `${labelOf(n)} is an XorGateway fork with no conditions; token would silently stall.`,
          nodeIds: [n.id],
        });
        continue;
      }
      const hasReal = conds.some(c => !isEmptyExpression(c.expression));
      const hasDefault = conds.some(c => isEmptyExpression(c.expression));
      if (!hasReal && !hasDefault) {
        issues.push({
          severity: 'error',
          rule: '4.10',
          message: `${labelOf(n)} is an XorGateway fork with no expressions and no default path.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_10_XorForkHasRoute);

// 4.11 — Non-empty expressions must be non-whitespace strings.
// Any condition whose expression is a string of only whitespace fails engine
// parse; empty-string is the legitimate default marker (handled elsewhere).
const rule_4_11_ExpressionNotWhitespace: ValidationRule = {
  id: '4.11',
  description: 'Gateway expressions must be either empty (default) or non-whitespace strings',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'XorGateway' && n.type !== 'InclusiveGateway') continue;
      const conds = getConditions(n);
      for (const cond of conds) {
        // Only catch the case: expression is non-empty string but all whitespace.
        // A truly empty string (length 0) is the default marker and must be
        // allowed. Typing is loose in storage; coerce safely.
        const expr = cond.expression;
        if (typeof expr !== 'string') continue;
        if (expr.length > 0 && expr.trim() === '') {
          issues.push({
            severity: 'error',
            rule: '4.11',
            message: `${labelOf(n)} condition path ${cond.path} has a whitespace-only expression; must be non-empty or fully empty (default).`,
            nodeIds: [n.id],
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_4_11_ExpressionNotWhitespace);

// 4.12 — skipped. Cannot statically verify expression result types (engine
// runs them through the script pool against a live ActivityContext).
// TODO: evaluate expression boolean-ness if a lightweight parser/linter for
// the script language becomes available in-editor.

// 4.13 — ParallelGateway fork should have >=2 outgoing. Warning.
const rule_4_13_ParallelForkFanout: ValidationRule = {
  id: '4.13',
  description: 'ParallelGateway fork should have at least 2 outgoing flows',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'ParallelGateway') continue;
      if (!isForkGateway(n)) continue;
      const outgoing = getFlowOutgoing(lookup, n.id).length;
      if (outgoing < 2) {
        issues.push({
          severity: 'warning',
          rule: '4.13',
          message: `${labelOf(n)} is a ParallelGateway fork with ${outgoing} outgoing flow(s); typically needs >=2.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_13_ParallelForkFanout);

// 4.14 — Parallel/Inclusive merge: non-recursive incoming count should be >=2.
// Engine's CanJoin = inputs - recursive; with 1 it is trivially joined.
const rule_4_14_MergeFanIn: ValidationRule = {
  id: '4.14',
  description: 'Parallel/Inclusive merge should have at least 2 non-recursive incoming flows',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'ParallelGateway' && n.type !== 'InclusiveGateway') continue;
      if (!isMergeGateway(n)) continue;
      const incoming = getFlowIncoming(lookup, n.id).length;
      if (incoming < 2) {
        // Note: getIncoming shows total incl. recursive; use total only for
        // context in the message, not for the rule decision.
        const totalIn = getIncoming(lookup, n.id).length;
        issues.push({
          severity: 'warning',
          rule: '4.14',
          message: `${labelOf(n)} is a ${n.type} merge with ${incoming} non-recursive incoming (${totalIn} total); typically needs >=2.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_4_14_MergeFanIn);
